import {
    Stack,
    Tabs,
    useMantineTheme,
    Box,
    Center,
    Button,
    Divider,
    Loader,
    Card,
    Text,
    Anchor,
    Space,
    Title,
    Group,
    Tooltip,
    Grid,
} from '@mantine/core';
import { AnchorButton, useResizeNeeded } from '@root/Design/Primitives';
import { useDi, useDiContainer, useDiMemo } from '@root/Services/DI';
import { useEvent, useEventValue } from '@root/Services/EventEmitter';
import { useNav } from '@root/Services/NavigationService';
import { endpoint } from '@root/Services/Router/EndpointRegistry';
import { ReactNode, useEffect, useMemo } from 'react';
import { InvoiceSpendAllocation, ShowbackSummary } from './Summary/ShowbackSummary';
import styled from '@emotion/styled';
import { ChevronRight, Settings } from 'tabler-icons-react';
import { ShowbackModel } from './Models';
import { AllocationDimensionDetailsDrawer, ShowbackMonthPicker } from './Common';
import { AllocationRules } from './AllocationRules';
import { ReconciliationGrid } from './Reconciliation/ReconciliationGrid';
import { withSchemaPreloader } from '@root/Components/Invoices/SchemaPreloader';
import { useLink } from '@root/Services/Router/Router';
import { ShowbackSummaryModel } from './Summary/ShowbackSummaryModel';
import { FormatService, NamedFormats } from '@root/Services/FormatService';
import { FillerSwitch } from '@root/Design/Filler';
import { useNavState } from '@root/Services/Router/NavState';
import { setLightness } from '@root/Design/Colors';

export function Showback() {
    //Show summary based on allocation dimension
    const nav = useNav();
    const model = useDiMemo(ShowbackModel);

    const { month } = useNavState({ month: '' });
    useEffect(() => {
        model.handleMonthNavUpdate(month);
    }, [month]);

    const showbackConfigured = useEventValue(model.showbackConfigured);
    const resizeNeeded = useResizeNeeded();

    const handleTabChange = (tabName: string) => {
        nav.mergeParams({ tab: tabName });
        resizeNeeded();
    };

    useEffect(() => () => model.dispose(), [model]);

    const selectedMonth = useEventValue(model.selectedMonthChanged);
    const unavailable = model.availableMonths.length === 0;
    if (showbackConfigured === undefined) {
        return <ShowbackLoading />;
    } else if (unavailable) {
        return <ShowbackUnavailable noData={model.availableMonths.length === 0} />;
    } else if (!showbackConfigured) {
        return <InitialSetupShowback model={model} />;
    } else {
        const { tab } = nav.getData('tab');
        const activeTab = tab ?? 'summary';
        return (
            <>
                <Tabs
                    key={model.selectedAllocationDimension?.DimensionName ?? ''}
                    defaultValue="summary"
                    value={activeTab}
                    color="gray"
                    variant="pills"
                    onTabChange={handleTabChange}
                    sx={{
                        flex: 1,
                        display: 'flex',
                        height: '100%',
                        flexDirection: 'column',
                        minHeight: 0,
                        ['[role=tabpanel]']: { flex: 1, minHeight: 0 },
                    }}
                >
                    <ShowbackHeaderBar>
                        <Tabs.List>
                            <Tabs.Tab data-atid="ShowbackSummaryTab" value="summary">
                                Summary
                            </Tabs.Tab>

                            {/* temporarily hide reconciliation page */}
                            {/* <Tabs.Tab data-atid="ShowbackReconciliationTab" value="reconciliation">
                                Reconciliation
                            </Tabs.Tab> */}

                            <Tabs.Tab data-atid="ShowbackAllocationRulesTab" value="allocationRules">
                                Allocation Rules
                            </Tabs.Tab>
                        </Tabs.List>
                        <Group>
                            <InvoiceSpendSummaryAllocation model={model} />
                        </Group>
                        <Group>
                            <Box>
                                {model.availableMonths.length ? (
                                    <ShowbackMonthPicker month={selectedMonth!} onChange={model.handleMonthSelected} months={model.availableMonths} />
                                ) : null}
                            </Box>
                        </Group>
                    </ShowbackHeaderBar>
                    <Divider color="gray.4" />
                    <Tabs.Panel value="summary">
                        {activeTab !== 'summary' ? null : (
                            <ShowbackPage>
                                <ShowbackSummary model={model} />
                            </ShowbackPage>
                        )}
                    </Tabs.Panel>
                    <Tabs.Panel value="reconciliation">
                        {' '}
                        {activeTab !== 'reconciliation' ? null : (
                            <ShowbackPage>
                                <ReconciliationGrid persistenceKey="ReconciliationGrid" model={model} />
                            </ShowbackPage>
                        )}
                    </Tabs.Panel>
                    <Tabs.Panel value="allocationRules">
                        {' '}
                        {activeTab !== 'allocationRules' ? null : <AllocationRules model={model}></AllocationRules>}
                    </Tabs.Panel>
                </Tabs>
                <AllocationDimensionDetailsDrawer settingsRequestedEvt={model.showbackSettingsRequested} onSave={model.saveDimension} />
            </>
        );
    }
}
endpoint('showback-overview', withSchemaPreloader(Showback), 'Showback');

export function ShowbackPage({ children }: { children: ReactNode }) {
    const theme = useMantineTheme();
    return (
        <Box p="xl" sx={{ height: '100%', overflow: 'auto', background: theme.colors.gray[2] }}>
            {children}
        </Box>
    );
}

export function InitialSetupShowback({ model }: { model: ShowbackModel }) {
    const theme = useMantineTheme();
    return (
        <SplashBg>
            <Box p="xl">
                <Center sx={{ height: 600 }}>
                    <Stack align="center">
                        <Button
                            radius="xl"
                            size="xl"
                            variant="outline"
                            color="primary"
                            rightIcon={<ChevronRight />}
                            onClick={model.createAllocationDimension}
                            sx={{ [':hover']: { background: theme.colors.gray[2] } }}
                        >
                            To begin, select your Allocation Dimension
                        </Button>
                    </Stack>
                </Center>
            </Box>
            <AllocationDimensionDetailsDrawer settingsRequestedEvt={model.showbackSettingsRequested} onSave={model.saveDimension} />
        </SplashBg>
    );
}

export function ShowbackLoading() {
    return (
        <SplashBg>
            <Center sx={{ height: '100%' }}>
                <Stack justify="center" align="center" sx={{ height: 100, width: 100, background: '#fff8', borderRadius: 100 }}>
                    <Loader size="xl" />
                </Stack>
            </Center>
        </SplashBg>
    );
}

export function ShowbackUnavailable({ noData, azureOnly }: { noData?: boolean; azureOnly?: boolean }) {
    const link = useLink();

    const message = !noData && 'Showback features are unavailable until invoice data has been collected. Please try again later, or';
    return (
        <SplashBg>
            <Center sx={{ height: '100%' }}>
                <Card shadow="md" radius="lg" withBorder p="lg" sx={{ width: 600 }}>
                    <Title order={3}>Service Unavailable</Title>
                    <Space h="sm" />
                    <Text>{message}</Text>
                    <Text>
                        <Anchor {...link('connections')}>Connect a Cloud Provider</Anchor>.
                    </Text>
                </Card>
            </Center>
        </SplashBg>
    );
}

export const SplashBg = styled.div`
    height: 100%;
    background-color: #f3f4f8;
    background-image: url('/assets/showback/showback-art-background.jpg');
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
`;

export const ShowbackHeaderBar = styled.div`
    height: 70px;
    padding: 0 ${(p) => 32}px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: ${(p) => p.theme.colors.gray[2]};
`;

export function InvoiceSpendSummaryAllocation({ model }: { model: ShowbackModel }) {
    const container = useDiContainer();
    const summaryModel = useMemo(() => container.resolve(ShowbackSummaryModel).setSbModel(model), [model]);
    useEffect(() => summaryModel.init(), [summaryModel]);
    useEvent(model.rulesChanged);

    return <ShowbackSummaryCard summaryModel={summaryModel} />;
}

function ShowbackSummaryCard({ summaryModel }: { summaryModel: ShowbackSummaryModel }) {
    const summaryData = useEventValue(summaryModel.dataChanged);
    const loading = useEventValue(summaryModel.loading);

    const data = useMemo(() => {
        if (!summaryData?.stats) {
            return undefined;
        }
        const { untagged, total } = summaryData.stats;
        if (!total) {
            return undefined;
        }
        const covered = total - untagged;
        const uncovered = untagged;
        const totalForPct = covered + Math.abs(uncovered);
        const coveredPct = covered / totalForPct;
        const uncoveredPct = uncovered / totalForPct;
        return {
            covered,
            uncovered,
            coveredPct,
            uncoveredPct,
            values: [
                { id: 'Allocated', value: covered },
                { id: 'Unallocated', value: uncovered },
            ],
        };
    }, [summaryData]);
    const theme = useMantineTheme();
    const allocColor = theme.colors.success[4];
    const unallocColor = theme.colors.warning[5];
    const fmtSvc = useDi(FormatService);

    return (
        <Box>
            <FillerSwitch loading={!!loading} noData={!data}>
                {() => (
                    <Card radius="md" withBorder shadow="md" p={12}>
                        <Group>
                            <ShowbackKeyCard
                                label={`Allocated (${fmtSvc.formatPercent(data!.coveredPct)})`}
                                amount={data!.covered}
                                color={allocColor}
                                moneyFormat={summaryModel.showbackModel.getMoneyFormatUserPref()}
                            />
                            <ShowbackKeyCard
                                label="Unallocated"
                                align="right"
                                amount={data!.uncovered}
                                color={unallocColor}
                                moneyFormat={summaryModel.showbackModel.getMoneyFormatUserPref()}
                            />
                        </Group>
                    </Card>
                )}
            </FillerSwitch>
        </Box>
    );
}

function ShowbackKeyCard(props: { align?: 'right' | 'left'; color: string; label: string; amount: number; moneyFormat: NamedFormats }) {
    const fmtSvc = useDi(FormatService);
    const { align, label, amount, moneyFormat } = props;
    return (
        <Box py={5} px={16} sx={{ borderRadius: 6, minWidth: 140 }}>
            <Text size="xs" align={align} weight="400">
                {label}
            </Text>
            <Text weight="bolder" align={align}>
                {fmtSvc.format(moneyFormat, amount)}
            </Text>
        </Box>
    );
}
